import { useMemo } from 'react'
import {
  GetLiveMonitorBookParticipantsSubscription,
  GetLiveMonitorBookParticipantsSubscriptionVariables,
  GetLiveMonitorReaderParticipantsSubscription,
  GetLiveMonitorReaderParticipantsSubscriptionVariables,
} from '../../../generated/graphql'
import { readingStatePriority } from '../../readingRoom/hooks/useLiveParticipants'
import { HLO } from '../../../types/global'
import resolveParticipationEventMeaning from '../../../util/resolveParticipationEventMeaning'
import GetLiveMonitorBookParticipants from '../graphql/subscriptions/GetLiveMonitorBookParticipants'
import GetLiveMonitorReaderParticipants from '../graphql/subscriptions/GetLiveMonitorReaderParticipants'
import useManualSubscription from './useManualSubscription'

export type ParticipantPresence = { name: string; isPresent: boolean }
export type PresenceByReadingId = Record<
  string,
  Record<string, ParticipantPresence>
>
export type SignedUpReader = { userId: string; name: string }
export type SignedUpByReadingId = Record<string, SignedUpReader[]>

export type UseLiveCallMonitorParticipantsConfig = {
  readingIds: string[]
  skip?: boolean
}

// Mirrors src/features/readingRoom/hooks/useLiveParticipants.ts's useParticipants,
// but grouped across many readings at once instead of a single one.
export default function useLiveCallMonitorParticipants({
  readingIds,
  skip = false,
}: UseLiveCallMonitorParticipantsConfig) {
  const readingIdsKey = readingIds.slice().sort().join(',')
  const isSkipped = skip || readingIds.length === 0

  const readerParticipants = useManualSubscription<
    GetLiveMonitorReaderParticipantsSubscription,
    GetLiveMonitorReaderParticipantsSubscriptionVariables
  >({
    query: GetLiveMonitorReaderParticipants,
    variables: { readingIds },
    skip: isSkipped,
    restartKey: readingIdsKey,
  })

  const bookParticipants = useManualSubscription<
    GetLiveMonitorBookParticipantsSubscription,
    GetLiveMonitorBookParticipantsSubscriptionVariables
  >({
    query: GetLiveMonitorBookParticipants,
    variables: { readingIds },
    skip: isSkipped,
    restartKey: readingIdsKey,
  })

  const { presenceByReadingId, signedUpByReadingId } = useMemo(() => {
    const result: PresenceByReadingId = {}
    const signedUp: SignedUpByReadingId = {}

    // Readers: isPresent comes from their participation event history (join/cancel).
    const readerEventsByReadingAndUser: Record<
      string,
      Record<
        string,
        {
          name: string
          events: { participationstate: string; created: string }[]
        }
      >
    > = {}

    readerParticipants.data?.view_reading_room_participants.forEach(
      ({ reading_id, user_id, participationstate, created, person_name }) => {
        if (
          !reading_id ||
          !user_id ||
          !participationstate ||
          !created ||
          !person_name
        ) {
          return
        }

        const readingGroup = readerEventsByReadingAndUser[reading_id] || {}
        const entry = readingGroup[user_id] || { name: person_name, events: [] }
        entry.events.push({ participationstate, created })
        readingGroup[user_id] = entry
        readerEventsByReadingAndUser[reading_id] = readingGroup
      },
    )

    Object.entries(readerEventsByReadingAndUser).forEach(
      ([readingId, users]) => {
        const readingPresence = result[readingId] || {}

        Object.entries(users).forEach(([userId, { name, events }]) => {
          const { isPresent, registered } = resolveParticipationEventMeaning({
            events: events.map(({ participationstate, created }) => ({
              participationstate: { name: participationstate },
              created,
            })),
          })

          readingPresence[userId] = { name, isPresent }

          if (registered) {
            signedUp[readingId] = [
              ...(signedUp[readingId] || []),
              { userId, name },
            ]
          }
        })

        result[readingId] = readingPresence
      },
    )

    // Books: isPresent comes from the reading's own current readingevent status.
    bookParticipants.data?.view_reading_room_book.forEach(
      ({ reading_id, user_id, person_name, reading }) => {
        if (!reading_id || !user_id || !person_name || !reading) return

        const readingEvents = reading.readingevents || []
        if (!Array.isArray(readingEvents) || readingEvents.length === 0) return

        const currentReadingEventStatusName = readingEvents
          .map((readingEvent) => readingEvent?.readingstate?.name)
          .filter((stateName): stateName is HLO.ReadingState =>
            Boolean(stateName),
          )
          .sort(
            (stateA, stateB) =>
              readingStatePriority[stateB] - readingStatePriority[stateA],
          )[0]

        if (!currentReadingEventStatusName) return

        const readingPresence = result[reading_id] || {}
        readingPresence[user_id] = {
          name: person_name,
          isPresent:
            currentReadingEventStatusName === 'In Progress' ||
            currentReadingEventStatusName === 'Ending',
        }
        result[reading_id] = readingPresence
      },
    )

    Object.values(signedUp).forEach((readers) =>
      readers.sort((a, b) => a.name.localeCompare(b.name)),
    )

    return { presenceByReadingId: result, signedUpByReadingId: signedUp }
  }, [
    readerParticipants.data?.view_reading_room_participants,
    bookParticipants.data?.view_reading_room_book,
  ])

  return {
    presenceByReadingId,
    signedUpByReadingId,
    loading: readerParticipants.loading || bookParticipants.loading,
    error: readerParticipants.error || bookParticipants.error,
  }
}
